"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, FolderSearch, Search, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  getCategories,
  searchCategories,
  getCategoryById,
} from "@/store/actions/category.actions";
import type { Category } from "@/types/models/category";

interface CategorySelectorProps {
  label?: string;
  value?: string[];
  onValueChange: (categoryIds: string[]) => void;
  placeholder?: string;
  error?: string;
  helperText?: string;
  maxSelections?: number;
  multiSelect?: boolean;
  seedCategories?: Category[];
}

interface CategoryOption {
  id: string;
  title: string;
  description?: string;
  status?: string;
  totalProducts: number;
}

const categoryToOption = (category: Category): CategoryOption => ({
  id: category.id || category.slug || category.title || "category",
  title: category.title || category.slug || category.id || "Category",
  description: category.description,
  status: category.status,
  totalProducts: category.total_products ?? category.products?.length ?? 0,
});

const mergeCategoryOptions = (
  existing: CategoryOption[],
  incoming: CategoryOption[],
): CategoryOption[] => {
  const byId = new Map<string, CategoryOption>();
  for (const item of existing) byId.set(item.id, item);
  for (const item of incoming) byId.set(item.id, item);
  return Array.from(byId.values());
};

const loadingPlaceholder = (id: string): CategoryOption => ({
  id,
  title: "Loading…",
  description: undefined,
  status: "DRAFT",
  totalProducts: 0,
});

const extractCategories = (response: any): Category[] => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  label = "Select Categories",
  value = [],
  onValueChange,
  placeholder = "Search and select categories",
  error,
  helperText,
  maxSelections = 10,
  multiSelect = true,
  seedCategories,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const didLoadInitialCatalogRef = useRef(false);
  const resolveAttemptedRef = useRef<Set<string>>(new Set());

  const displaySelected = useMemo(() => {
    return value.map((id) => {
      const category = categories.find((item) => item.id === id);
      return category ?? loadingPlaceholder(id);
    });
  }, [value, categories]);

  const loadInitialCategories = async () => {
    try {
      setLoading(true);
      const response = await getCategories({
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortOrder: "desc",
      });
      const options = extractCategories(response).map(categoryToOption);
      setCategories((prev) => mergeCategoryOptions(prev, options));
    } catch (error) {
      console.error("[CategorySelector] Failed to load initial categories", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!seedCategories?.length) return;
    const incoming = seedCategories.map(categoryToOption);
    setCategories((prev) => mergeCategoryOptions(prev, incoming));
  }, [seedCategories]);

  useEffect(() => {
    const missing = value.filter((id) => !categories.some((item) => item.id === id));
    const toResolve = missing.filter((id) => !resolveAttemptedRef.current.has(id));
    if (!toResolve.length) return;

    toResolve.forEach((id) => resolveAttemptedRef.current.add(id));
    let cancelled = false;

    void (async () => {
      const resolved = await Promise.all(
        toResolve.map(async (id) => {
          try {
            const category = await getCategoryById(id);
            return category?.id ? categoryToOption(category) : null;
          } catch (error) {
            console.warn("[CategorySelector] getCategoryById failed", { id, error });
            return null;
          }
        }),
      );

      if (cancelled) return;

      const resolvedOptions = resolved.filter(
        (item): item is CategoryOption => Boolean(item),
      );
      const fallback = toResolve
        .filter((id) => !resolvedOptions.some((item) => item.id === id))
        .map((id) => ({
          id,
          title: "Unavailable",
          description: undefined,
          status: "DRAFT",
          totalProducts: 0,
        }));

      setCategories((prev) =>
        mergeCategoryOptions(prev, [...resolvedOptions, ...fallback]),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [value, categories]);

  useEffect(() => {
    if (!open || didLoadInitialCatalogRef.current) return;
    didLoadInitialCatalogRef.current = true;
    void loadInitialCategories();
  }, [open]);

  const searchCategoriesApi = async (query: string) => {
    if (!query.trim()) {
      void loadInitialCategories();
      return;
    }

    try {
      setLoading(true);
      const response = await searchCategories({
        query,
        page: 1,
        pageSize: 20,
        sortBy: "created_at",
        sortOrder: "desc",
      });
      const options = extractCategories(response).map(categoryToOption);
      setCategories((prev) => mergeCategoryOptions(prev, options));
    } catch (error) {
      console.error("[CategorySelector] Failed to search categories", error);
      const filtered = categories.filter((category) =>
        category.title.toLowerCase().includes(query.toLowerCase()),
      );
      setCategories(filtered);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      void searchCategoriesApi(query);
    }, 300);
  };

  const handleCategorySelect = (category: CategoryOption) => {
    if (!multiSelect) {
      onValueChange([category.id]);
      setOpen(false);
      setSearchQuery("");
      return;
    }

    const isSelected = value.includes(category.id);
    let nextSelection: string[];

    if (isSelected) {
      nextSelection = value.filter((id) => id !== category.id);
    } else {
      if (value.length >= maxSelections) return;
      nextSelection = [...value, category.id];
    }

    onValueChange(nextSelection);
  };

  const handleRemoveCategory = (
    categoryId: string,
    e: React.MouseEvent | React.KeyboardEvent,
  ) => {
    e.stopPropagation();
    onValueChange(value.filter((id) => id !== categoryId));
  };

  return (
    <div className="space-y-1">
      <label className="block text-[13px] font-normal text-muted-foreground">
        {label}
      </label>
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              className="w-full h-auto min-h-[38px] justify-between bg-transparent hover:bg-transparent px-4 py-2 font-normal"
            >
              <div className="flex-1 text-left">
                {displaySelected.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {displaySelected.map((category) => (
                      <span
                        key={category.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
                      >
                        {category.title}
                        <button
                          type="button"
                          onClick={(e) => handleRemoveCategory(category.id, e)}
                          className="hover:text-destructive ml-1"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </div>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <div className="flex items-center border-b px-3 py-2 gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search categories..."
                className="w-full bg-transparent outline-none text-sm"
              />
            </div>
            <div className="max-h-72 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-sm text-muted-foreground">
                  Loading categories...
                </div>
              ) : categories.length > 0 ? (
                categories.map((category) => {
                  const selected = value.includes(category.id);
                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleCategorySelect(category)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted transition-colors",
                        selected && "bg-primary/5",
                      )}
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">
                        <FolderSearch className="h-4 w-4" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-sm font-medium">
                          {category.title}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {category.status || "DRAFT"} · {category.totalProducts} products
                        </span>
                      </span>
                      {selected && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  );
                })
              ) : (
                <div className="p-4 text-sm text-muted-foreground">
                  No categories found.
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {helperText && !error && (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};
